import BackButton from '@/components/buttons/BackButton';
import { FF_ENABLE_CONCESSION_DISCOUNT_BANNER, FF_ENABLE_MFA_SETUP } from '@/constants/featureFlags';
import { usePortalPartnerBrand } from '@/hooks/portal-partner-brand';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import PasswordReset from '@/pages/profile/PasswordReset';
import ProfileFormLoadingSkeleton from '@/pages/profile/ProfileFormLoadingSkeleton';
import { useUserProfileData } from '@montugroup/circuit-shared/hooks/user/useGetUserProfile';
import { Alert, AlertTitle, Box, Link, Stack, Typography, ViewObserver } from '@montugroup/design-system';
import { BrandThemeProvider, uMeds } from '@montugroup/themes';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import { styled } from '@mui/material';
import { DateTime } from 'luxon';
import { lazy, Suspense, useEffect, useState } from 'react';
import { MFAEnrolment } from './MFAEnrolment';
import ShippingAddressForm from './ShippingAddressForm';
import ShippingAddressLoadingSkeleton from './ShippngAddressLoadingSkeleton';

const ProfileForm = lazy(() => import('@/pages/profile/ProfileForm'));

interface IAlert {
  severity: 'success' | 'error';
  message: string;
}

export const AlertResponse = styled(Alert)<{ type: 'success' | 'error' }>(({ theme, type }) => ({
  backgroundColor: type === 'success' ? theme.palette.success.light : theme.palette.error.light,
  color: type === 'success' ? theme.palette.success.dark : theme.palette.error.dark,
  fontWeight: theme.typography.fontWeightBold,
  margin: theme.spacing(4, 0),
  width: '100%',
  '& .MuiAlert-message': {
    padding: theme.spacing(2.5, 0),
  },
  [theme.breakpoints.up('md')]: {
    minWidth: '375px',
  },
}));

const ConcessionBanner = ({ expiryDate }: { expiryDate: string }) => (
  <ViewObserver id="concession-discount-banner">
    <Alert
      severity="info"
      icon={<SellOutlinedIcon />}
      sx={{
        width: '100%',
        my: 6,
      }}
      data-testid="concession-discount-banner"
    >
      <AlertTitle color="info">Concession status active and discounts applied</AlertTitle>
      <Typography variant="inherit">Concession card expiry date: {expiryDate}</Typography>
      <Typography variant="inherit">
        <Link
          href="https://support.umeds.com.au/hc/en-au/articles/10802598035599-Concession-benefits"
          underline="always"
          color="inherit"
        >
          Learn more
        </Link>{' '}
        about uploading a new concession card.
      </Typography>
    </Alert>
  </ViewObserver>
);

export default function PatientProfile() {
  const { data, isLoading, isSuccess, isError, getUserState } = useUserProfileData();
  const [alert, setAlert] = useState<IAlert>({ severity: 'success', message: '' });
  const { flags } = useFeatureFlags();

  const { Logo: PartnerLogo } = usePortalPartnerBrand();

  const isConcessionEnabled = flags[FF_ENABLE_CONCESSION_DISCOUNT_BANNER];
  const shouldShowConcessionBanner = (isConcessionEnabled && data?.concession_status?.valid) || false;
  const concessionExpiryDate = data?.concession_status?.expiry_date
    ? DateTime.fromISO(data.concession_status.expiry_date).toFormat('MMM dd, yyyy')
    : '-';

  const handleClose = () => {
    setAlert({ severity: 'success', message: '' });
  };

  const profileFormContent = () => {
    if (isLoading) {
      return <ProfileFormLoadingSkeleton />;
    }
    if (isSuccess && data) {
      return (
        <Suspense fallback={<ProfileFormLoadingSkeleton />}>
          <ProfileForm data={data} />
        </Suspense>
      );
    }
    return null;
  };

  useEffect(() => {
    if (!data && !isLoading && isError) {
      setAlert({ severity: 'error', message: 'Error retrieving profile data' });
    }
  }, [data, isLoading, isError]);

  return (
    <Box>
      <Stack direction="row">
        <BackButton />
        <Typography
          variant="h5"
          component="h1"
          sx={(theme) => ({ color: theme.palette.primary.main, lineHeight: theme.spacing(10) })}
        >
          Profile
        </Typography>
      </Stack>
      {shouldShowConcessionBanner && <ConcessionBanner expiryDate={concessionExpiryDate} />}
      {alert.message && (
        <AlertResponse severity={alert.severity} type={alert.severity} onClose={handleClose}>
          {alert.message}
        </AlertResponse>
      )}
      {profileFormContent()}
      <Typography
        variant="subtitle1"
        component="h2"
        sx={(theme) => ({
          color: theme.palette.primary.main,
          lineHeight: theme.spacing(10),
          paddingTop: theme.spacing(10),
        })}
      >
        Shipping Address
      </Typography>
      <BrandThemeProvider theme={uMeds}>
        <Typography variant="body2" sx={(theme) => ({ color: theme.palette.primary.main })}>
          Powered by <PartnerLogo fillColor="currentColor" sx={{ width: '4rem' }} />
        </Typography>
      </BrandThemeProvider>
      {isLoading ? (
        <ShippingAddressLoadingSkeleton />
      ) : (
        data && <ShippingAddressForm userData={data} getUserState={getUserState} />
      )}
      <PasswordReset />
      {flags[FF_ENABLE_MFA_SETUP] && <MFAEnrolment />}
    </Box>
  );
}
